import { SelectOption } from '@diamond/shared/types';
import {
  ChakraStylesConfig as StylesConfig,
  components,
  MultiValueProps,
  Props,
  Select as ReactSelect,
} from 'chakra-react-select';
import { CSSProperties } from 'react';

export const reactSelectStyles: StylesConfig<SelectOption, boolean> = {
  container(base) {
    return {
      ...base,
      minWidth: 160,
    };
  },
  menu(base) {
    return {
      ...base,
      // Ensure the dropdown menu appears above modal overlay/content
      // Align with Chakra theme: popover is 1500, modal is 1400
      zIndex: 2000,
    };
  },
  // menuPortal(base) {
  //   return {
  //     ...base,
  //     // Portal container should sit above Chakra Modal overlay/content
  //     // Choose 1500 (Chakra popover layer) > modal(1400)
  //     zIndex: 1500,
  //   };
  // },
  indicatorSeparator() {
    return {
      display: 'none',
    };
  },
  valueContainer(base) {
    return {
      ...base,
      flexWrap: 'nowrap',
    };
  },
  control(base, state) {
    return {
      ...base,
      minHeight: 10,
      borderRadius: 'var(--chakra-radii-md)',
      borderColor: state.isFocused
        ? 'var(--chakra-colors-blue-500)'
        : 'var(--chakra-colors-gray-300)',
      boxShadow: state.isFocused
        ? '0 0 0 1px var(--chakra-colors-blue-500)'
        : 'none',
      ':hover': {
        borderColor: state.isFocused
          ? 'var(--chakra-colors-blue-500)'
          : 'var(--chakra-colors-gray-400)',
      },
      ...(state.isDisabled && {
        borderColor: 'var(--chakra-colors-gray-300)',
        opacity: 1,
        cursor: 'not-allowed',
      }),
    };
  },
  multiValue(base, state) {
    return {
      ...base,
      backgroundColor: state.isDisabled
        ? 'var(--chakra-colors-gray-300)'
        : 'var(--chakra-colors-gray-200)',
      borderRadius: 'var(--chakra-radii-md)',
    };
  },
  multiValueLabel(base, state) {
    return {
      ...base,
      color: state.isDisabled
        ? 'var(--chakra-colors-gray-600)'
        : 'var(--chakra-colors-gray-700)',
      fontSize: '0.875rem',
      paddingLeft: '8px',
      paddingRight: '4px',
    };
  },
  multiValueRemove(base, state) {
    return {
      ...base,
      color: 'var(--chakra-colors-gray-500)',
      paddingLeft: '4px',
      paddingRight: '8px',
      ':hover': {
        backgroundColor: 'var(--chakra-colors-gray-300)',
        color: 'var(--chakra-colors-gray-700)',
      },
      ...(state.isDisabled && {
        display: 'none',
      }),
    };
  },
  placeholder(base, state) {
    return {
      ...base,
      ...(state.isDisabled && {
        color: 'var(--chakra-colors-gray-500)',
      }),
    };
  },
  input(base, state) {
    return {
      ...base,
      ...(state.isDisabled && {
        color: 'var(--chakra-colors-gray-600)',
      }),
    };
  },
};

const MoreSelectedBadge = ({ items }: { items: string[] }) => {
  const style: CSSProperties = {
    marginLeft: 'auto',
    background: 'var(--chakra-colors-gray-200)',
    fontFamily: 'inherit',
    order: 99,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    borderRadius: 'var(--chakra-radii-md)',
    color: 'var(--chakra-colors-gray-700)',
    fontSize: '0.875rem',
    padding: '2px 8px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
  };

  const title = items.join(', ');
  const length = items.length;
  const label = `+ ${length}`;

  return (
    <div style={style} title={title}>
      {label}
    </div>
  );
};

type Option = {
  label: string;
  value: string;
};

function MultiValue(props: MultiValueProps<Option, true>) {
  const { index, getValue } = props;
  const maxToShow = 3;
  const overflow = getValue()
    .slice(maxToShow)
    .map((x) => x.label);

  return index < maxToShow ? (
    <components.MultiValue {...props} />
  ) : index === maxToShow ? (
    <MoreSelectedBadge items={overflow} />
  ) : null;
}

export function MultiSelect(props: Props<Option, true>) {
  // Filter out selected options from the options list
  const selectedValues = Array.isArray(props.value)
    ? props.value.map((v) => v.value)
    : [];
  const filteredOptions = Array.isArray(props.options)
    ? props.options.filter((option) => !selectedValues.includes(option.value))
    : props.options;

  return (
    <ReactSelect
      isMulti
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      components={{ MultiValue }}
      // // Portal the menu to avoid clipping by overflow-hidden parents (e.g., modals)
      // menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
      // menuPosition="fixed"
      {...props}
      options={filteredOptions}
    />
  );
}
